/**
 * Denied Claims Tracker — /denied-claims
 *
 * Admin + Super User only. Mirrors Underpayment Recovery's layout — upload
 * the SP "Filed Claims" report, work the list of actionable claims
 * (Denied / Rejected / Error scrub / Action required / Info required),
 * mark them Resolved/Written Off to archive permanently so re-uploads
 * never resurrect closed items.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { fmtDate, fmtDateTime } from '../lib/date';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertTriangle, FileWarning, Search, X, Archive,
  Clock, Users, RefreshCw, ArrowUpDown,
  Flame, DollarSign, CheckSquare,
} from 'lucide-react';

const TABS = [
  { key: 'tracker',  label: 'Open Claims',       icon: ArrowUpDown },
  { key: 'terminal', label: 'Resolved / Closed', icon: Archive },
];

const int = (v) => (Number(v) || 0).toLocaleString();
const money = (v) => (Number(v) || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const moneyExact = (v) => (Number(v) || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

function Pill({ children, tone = '#685B72', testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest"
      style={{ background: `${tone}18`, color: tone }}
    >
      {children}
    </span>
  );
}

const SOURCE_STATUS_TONE = {
  'Denied':          '#8A3A3A',
  'Rejected':        '#8A3A3A',
  'Error scrub':     '#B08A3A',
  'Action required': '#B08A3A',
  'Info required':   '#685B72',
};

export default function DeniedClaims() {
  const [tab, setTab] = useState('tracker');
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Per-payer × age-bucket heatmap payload (iter 357).
  const [heatmap, setHeatmap] = useState(null);

  // filters
  const [q, setQ] = useState('');
  const [sourceStatus, setSourceStatus] = useState('all');
  const [workStatus, setWorkStatus] = useState('all');
  const [sort, setSort] = useState('age_desc');

  const [editing, setEditing] = useState(null); // row being worked on

  // bulk selection — Set of claim_keys currently checked in the tracker.
  // Cleared on tab switch / page refresh. Only applies to the Open Claims
  // view; terminal rows aren't selectable.
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const toggleSelect = (key) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleSelectAll = (allKeys) => setSelected(prev => {
    const allChecked = allKeys.every(k => prev.has(k));
    if (allChecked) return new Set();
    return new Set(allKeys);
  });

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/denied-claims/summary');
      setSummary(data);
    } catch { /* silent */ }
  }, []);
  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/denied-claims/config');
      setConfig(data);
    } catch { /* silent */ }
  }, []);
  const loadHeatmap = useCallback(async () => {
    try {
      const { data } = await api.get('/denied-claims/age-heatmap');
      setHeatmap(data);
    } catch { setHeatmap(null); }
  }, []);
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const path = tab === 'terminal' ? '/denied-claims/terminal' : '/denied-claims/tracker';
      const params = {};
      if (q) params.q = q;
      if (tab === 'tracker') {
        if (sourceStatus !== 'all') params.source_status = sourceStatus;
        if (workStatus !== 'all')   params.status = workStatus;
        params.sort = sort;
      }
      const { data } = await api.get(path, { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load claims');
    } finally { setLoading(false); }
  }, [tab, q, sourceStatus, workStatus, sort]);

  useEffect(() => { loadSummary(); loadConfig(); loadHeatmap(); }, [loadSummary, loadConfig, loadHeatmap]);
  useEffect(() => {
    const t = setTimeout(loadList, 200);
    return () => clearTimeout(t);
  }, [loadList]);

  const sourceStatuses = useMemo(() => {
    const set = new Set(items.map(x => x.source_status).filter(Boolean));
    (summary?.by_source_status || []).forEach(s => s.status && set.add(s.status));
    return Array.from(set).sort();
  }, [items, summary]);

  return (
    <div className="space-y-6 pb-24" data-testid="denied-claims-page">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-br from-[#8A3A3A] via-[#A14E55] to-[#B08A3A] px-8 py-7 text-white">
        <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80 flex items-center gap-1.5">
          <FileWarning size={12} /> Denied Claims Tracker
        </div>
        <h1 className="font-serif text-4xl mt-1">Work every denial. Resolve it. Keep it closed.</h1>
        <p className="text-[13px] mt-2 opacity-90 max-w-3xl">
          Auto-populated from the <b>Claims Report</b> upload on the Import page. Any claim whose Status is Denied,
          Rejected, Error scrub, Action required, or Info required lands here. Mark it <b>Resolved</b> or <b>Written
          Off</b> and it's archived permanently — tomorrow's upload cannot resurrect a closed claim.
        </p>
        <div className="mt-4 flex items-center gap-2 flex-wrap text-[11px] opacity-90">
          {summary?.last_upload?.uploaded_at ? (
            <span>
              Last refresh: {fmtDateTime(summary.last_upload.uploaded_at)}
              {summary.last_upload.auto_from_claims ? ' · auto · from Claims report upload' : ` · by ${summary.last_upload.uploaded_by || '—'}`}
            </span>
          ) : (
            <span className="italic">No claims data yet — upload a Claims Report via the Import page to populate.</span>
          )}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="dc-summary-tiles">
        <Card className="p-4 rounded-2xl bg-white border border-[#E8E5EC]">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#685B72]">Open claims</div>
          <div className="font-serif text-3xl text-[#2D2833] mt-1" data-testid="dc-tile-open">{int(summary?.open_count)}</div>
          <div className="text-[11px] text-[#685B72] mt-1 flex items-center gap-1"><Users size={11} /> {int(summary?.unique_clients)} clients</div>
        </Card>
        <Card className="p-4 rounded-2xl bg-white border border-[#E8E5EC]">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#685B72] flex items-center gap-1">
            <DollarSign size={11} /> $ at risk
          </div>
          <div className="font-serif text-3xl text-[#8A3A3A] mt-1" data-testid="dc-tile-at-risk">
            {money(summary?.total_at_risk)}
          </div>
          <div className="text-[11px] text-[#685B72] mt-1">
            {int(summary?.total_known_rows)} of {int(summary?.total_rows)} rows priced
          </div>
        </Card>
        <Card className="p-4 rounded-2xl bg-white border border-[#E8E5EC]">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#685B72]">Critically aged</div>
          <div className="font-serif text-3xl text-[#8A3A3A] mt-1" data-testid="dc-tile-critical">{int(summary?.critically_aged)}</div>
          <div className="text-[11px] text-[#685B72] mt-1 flex items-center gap-1"><AlertTriangle size={11} /> 60+ days old</div>
        </Card>
        <Card className="p-4 rounded-2xl bg-white border border-[#E8E5EC]">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#685B72]">30–60 days</div>
          <div className="font-serif text-3xl text-[#B08A3A] mt-1" data-testid="dc-tile-aged">{int(summary?.aged_30_60)}</div>
          <div className="text-[11px] text-[#685B72] mt-1 flex items-center gap-1"><Clock size={11} /> work soon</div>
        </Card>
        <Card className="p-4 rounded-2xl bg-white border border-[#E8E5EC]">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#685B72]">Top payer</div>
          <div className="font-serif text-xl text-[#2D2833] mt-1 truncate" data-testid="dc-tile-top-payer">
            {summary?.top_payers?.[0]?.payer || '—'}
          </div>
          <div className="text-[11px] text-[#685B72] mt-1">{int(summary?.top_payers?.[0]?.count)} claims</div>
        </Card>
      </div>

      {/* Hot spots — top 3 payer × source-status pairs. One-tap drill-in
          via the filter buttons on the chip. */}
      {(summary?.hot_pairs?.length || 0) > 0 && (
        <Card className="p-4 rounded-2xl bg-gradient-to-br from-[#FFF6DF] via-white to-white border border-[#B08A3A]/40" data-testid="dc-hot-spots">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#8A3A3A] mb-2 flex items-center gap-1.5">
            <Flame size={12} /> Hot spots — top payer × status pairs to attack
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {summary.hot_pairs.map((hp, i) => (
              <button
                key={`${hp.payer}-${hp.source_status}-${i}`}
                onClick={() => { setTab('tracker'); setSourceStatus(hp.source_status); setQ(hp.payer); }}
                className="text-left rounded-xl border border-[#8A3A3A]/20 bg-white hover:bg-[#FFF6DF] px-4 py-3 transition-colors"
                data-testid={`dc-hotspot-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[13px] font-semibold text-[#2D2833] truncate">{hp.payer}</div>
                  <Pill tone={SOURCE_STATUS_TONE[hp.source_status] || '#685B72'}>{hp.source_status}</Pill>
                </div>
                <div className="flex items-baseline gap-3 mt-2">
                  <div><span className="font-serif text-2xl text-[#8A3A3A]">{int(hp.count)}</span><span className="text-[11px] text-[#685B72] ml-1">claims</span></div>
                  {hp.amount > 0 && (
                    <div><span className="font-serif text-base text-[#2D2833]">{money(hp.amount)}</span><span className="text-[11px] text-[#685B72] ml-1">at risk</span></div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Denial Age Heatmap — per-payer × week-bucket matrix. Cell click
          filters the Open Claims table to that payer and sends the admin
          directly to the aging rows that need attention. */}
      <DenialAgeHeatmap
        data={heatmap}
        onCellClick={(payer /* , bucketKey */) => {
          setTab('tracker');
          setQ(payer);
          setSort('age_desc');
        }}
      />

      {/* Source status breakdown */}
      {summary?.by_source_status?.length > 0 && (
        <Card className="p-4 rounded-2xl bg-white border border-[#E8E5EC]" data-testid="dc-source-breakdown">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#685B72] mb-2">
            By source status
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {summary.by_source_status.map(s => (
              <button
                key={s.status}
                onClick={() => { setTab('tracker'); setSourceStatus(s.status === sourceStatus ? 'all' : s.status); }}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  sourceStatus === s.status ? 'bg-[#2D2833] text-white border-[#2D2833]' : 'bg-white hover:bg-[#F7EFDA]'
                }`}
                style={{ borderColor: sourceStatus === s.status ? '#2D2833' : `${SOURCE_STATUS_TONE[s.status] || '#685B72'}66` }}
                data-testid={`dc-filter-source-${s.status.replace(/\W+/g, '_')}`}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: SOURCE_STATUS_TONE[s.status] || '#685B72' }}
                />
                {s.status} · {int(s.count)}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#E8E5EC]">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`dc-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                active ? 'border-[#8A3A3A] text-[#2D2833]' : 'border-transparent text-[#685B72] hover:text-[#2D2833]'
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="p-3 rounded-2xl bg-white border border-[#E8E5EC] flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#685B72]" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search client, payer, claim ID, notes…"
            className="pl-9 rounded-xl border-[#E8E5EC]"
            data-testid="dc-search"
          />
        </div>
        {tab === 'tracker' && (
          <>
            <Select value={sourceStatus} onValueChange={setSourceStatus}>
              <SelectTrigger className="w-[170px] rounded-xl" data-testid="dc-filter-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All source statuses</SelectItem>
                {sourceStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={workStatus} onValueChange={setWorkStatus}>
              <SelectTrigger className="w-[170px] rounded-xl" data-testid="dc-filter-work"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All work statuses</SelectItem>
                {(config?.statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[170px] rounded-xl" data-testid="dc-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="age_desc">Oldest first</SelectItem>
                <SelectItem value="age_asc">Newest first</SelectItem>
                <SelectItem value="client_asc">Client A→Z</SelectItem>
                <SelectItem value="payer_asc">Payer A→Z</SelectItem>
                <SelectItem value="status_asc">Status A→Z</SelectItem>
                <SelectItem value="submitted_desc">Submit date ↓</SelectItem>
              </SelectContent>
            </Select>
            {(q || sourceStatus !== 'all' || workStatus !== 'all') && (
              <button
                onClick={() => { setQ(''); setSourceStatus('all'); setWorkStatus('all'); }}
                className="text-xs text-[#685B72] hover:text-[#2D2833] underline"
                data-testid="dc-clear-filters"
              >
                Clear filters
              </button>
            )}
          </>
        )}
        <button
          onClick={() => { loadSummary(); loadList(); loadHeatmap(); }}
          className="ml-auto h-9 w-9 rounded-full flex items-center justify-center text-[#685B72] hover:bg-[#F1EDF5]"
          title="Refresh"
          data-testid="dc-refresh"
        >
          <RefreshCw size={14} />
        </button>
      </Card>

      {/* Table */}
      <Card className="rounded-2xl bg-white border border-[#E8E5EC] overflow-hidden">
        {/* Bulk-action toolbar — appears whenever at least one open row is
            selected. Mirrors the Underpayment page's bulk pattern. */}
        {tab === 'tracker' && selected.size > 0 && (
          <div className="sticky top-0 z-20 bg-[#2D2833] text-white px-4 py-2 flex items-center gap-3 text-sm shadow-md" data-testid="dc-bulk-toolbar">
            <CheckSquare size={14} />
            <span><b>{selected.size}</b> selected</span>
            <button
              className="ml-auto bg-white text-[#2D2833] hover:bg-[#F7EFDA] rounded-full px-3 py-1 text-xs font-semibold"
              data-testid="dc-bulk-resolve"
              onClick={() => setBulkOpen(true)}
            >
              Bulk Resolve / Write Off…
            </button>
            <button
              className="text-white/70 hover:text-white text-xs"
              onClick={() => setSelected(new Set())}
              data-testid="dc-bulk-clear"
            >
              Clear
            </button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dc-table">
            <thead>
              <tr className="bg-[#FAF8FB] text-[#685B72] text-left">
                {tab === 'tracker' && (
                  <th className="pl-4 pr-1 py-3 w-8">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[#8A3A3A]"
                      checked={items.length > 0 && items.every(r => selected.has(r.claim_key))}
                      onChange={() => toggleSelectAll(items.map(r => r.claim_key))}
                      data-testid="dc-select-all"
                      aria-label="Select all"
                    />
                  </th>
                )}
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Submitted</th>
                <th className="px-4 py-3 font-semibold">Source</th>
                <th className="px-4 py-3 font-semibold">Payer</th>
                <th className="px-4 py-3 font-semibold">Claim ID</th>
                {tab === 'tracker' ? (
                  <>
                    <th className="px-4 py-3 font-semibold">Age</th>
                    <th className="px-4 py-3 font-semibold">Amount</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Owner</th>
                    <th className="px-4 py-3 font-semibold">Follow-up</th>
                  </>
                ) : (
                  <>
                    <th className="px-4 py-3 font-semibold">Final</th>
                    <th className="px-4 py-3 font-semibold">Resolved</th>
                    <th className="px-4 py-3 font-semibold">By</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={tab === 'tracker' ? 11 : 9} className="px-4 py-10 text-center text-[#685B72]">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={tab === 'tracker' ? 11 : 9} className="px-4 py-16 text-center">
                    <div className="text-[#685B72] text-sm">
                      {tab === 'terminal'
                        ? 'No resolved claims yet.'
                        : 'No open denied claims — upload a Filed Claims report to populate.'}
                    </div>
                  </td>
                </tr>
              ) : items.map(row => (
                <tr
                  key={row.claim_key}
                  className={`border-t border-[#E8E5EC] ${tab === 'tracker' ? 'hover:bg-[#FAF8FB]' : ''}`}
                  data-testid={`dc-row-${row.claim_key}`}
                >
                  {tab === 'tracker' && (
                    <td className="pl-4 pr-1 py-3 w-8" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#8A3A3A]"
                        checked={selected.has(row.claim_key)}
                        onChange={() => toggleSelect(row.claim_key)}
                        data-testid={`dc-select-${row.claim_key}`}
                        aria-label={`Select ${row.client}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium text-[#2D2833] cursor-pointer" onClick={() => tab === 'tracker' && setEditing(row)}>{row.client}</td>
                  <td className="px-4 py-3 text-[#685B72] cursor-pointer" onClick={() => tab === 'tracker' && setEditing(row)}>{row.date_submitted ? fmtDate(row.date_submitted) : '—'}</td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => tab === 'tracker' && setEditing(row)}>
                    <Pill tone={SOURCE_STATUS_TONE[row.source_status] || '#685B72'} testid={`dc-source-${row.claim_key}`}>
                      {row.source_status}
                    </Pill>
                  </td>
                  <td className="px-4 py-3 text-[#685B72] cursor-pointer" onClick={() => tab === 'tracker' && setEditing(row)}>
                    <div className="font-medium text-[#2D2833]">{row.payer_name || '—'}</div>
                    {row.raw_payer && row.raw_payer !== row.payer_name && (
                      <div className="text-[10px] text-[#685B72]/70 font-mono">{row.raw_payer}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#685B72] cursor-pointer" onClick={() => tab === 'tracker' && setEditing(row)} data-testid={`dc-claim-id-${row.claim_key}`}>
                    {row.payer_claim || row.clearinghouse_ref || '—'}
                  </td>
                  {tab === 'tracker' ? (
                    <>
                      <td className="px-4 py-3 cursor-pointer" onClick={() => setEditing(row)}>
                        <span className={`font-mono text-xs ${row.days_aged >= 60 ? 'text-[#8A3A3A] font-bold' : row.days_aged >= 30 ? 'text-[#B08A3A]' : 'text-[#685B72]'}`}>
                          {row.days_aged}d
                        </span>
                      </td>
                      <td className="px-4 py-3 cursor-pointer" onClick={() => setEditing(row)}>
                        {row.billed_amount > 0
                          ? <span className="font-mono text-xs text-[#2D2833]">{money(row.billed_amount)}</span>
                          : <span className="text-[11px] italic text-[#685B72]/60">add $</span>}
                      </td>
                      <td className="px-4 py-3 text-[#2D2833] cursor-pointer" onClick={() => setEditing(row)}>{row.status || 'New'}</td>
                      <td className="px-4 py-3 text-[#685B72] cursor-pointer" onClick={() => setEditing(row)}>{row.owner || '—'}</td>
                      <td className="px-4 py-3 text-[#685B72] cursor-pointer" onClick={() => setEditing(row)}>{row.next_follow_up ? fmtDate(row.next_follow_up) : '—'}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-[#2D2833]">{row.final_status || '—'}</td>
                      <td className="px-4 py-3 text-[#685B72]">{row.resolution_date ? fmtDate(row.resolution_date) : '—'}</td>
                      <td className="px-4 py-3 text-[#685B72] text-xs">{row.archived_by || '—'}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit dialog */}
      <EditClaimDialog
        row={editing}
        config={config}
        onClose={() => setEditing(null)}
        onSaved={(archived) => {
          setEditing(null);
          loadSummary();
          loadList();
          if (archived) toast.success('Claim archived to Resolved history');
        }}
      />

      {/* Bulk Resolve dialog */}
      <BulkResolveDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        selectedKeys={Array.from(selected)}
        config={config}
        rows={items.filter(r => selected.has(r.claim_key))}
        onDone={(n) => {
          setBulkOpen(false);
          setSelected(new Set());
          toast.success(`${n} claim${n === 1 ? '' : 's'} archived.`);
          loadSummary(); loadList();
        }}
      />
    </div>
  );
}


// ─── Denial Age Heatmap (iter 357) ───────────────────────────────────
// Per-payer × week-bucket matrix. Cell intensity is driven by that
// cell's share of the row total; the 60+ column always carries an
// extra rose tone because that's the critical-aging bucket. Clicking
// any cell drills the Open Claims table to that payer.

function DenialAgeHeatmap({ data, onCellClick }) {
  if (!data || !(data.payers || []).length) {
    return (
      <Card className="p-6 rounded-2xl bg-white border border-[#E8E5EC] text-center text-[#685B72] text-sm" data-testid="dc-heatmap-empty">
        No open denied claims to heatmap yet.
      </Card>
    );
  }
  const buckets = data.buckets || [];

  // Max cell value sets the saturation ceiling so a 1-claim cell doesn't
  // visually equal a 100-claim cell. Capped at 1 to avoid div-by-zero.
  const maxCell = Math.max(
    1,
    ...data.payers.flatMap(p => buckets.map(b => p.cells[b.key] || 0)),
  );

  const toneFor = (count, isCritical) => {
    if (!count) return { bg: '#FAF8FB', color: '#B9B0C6' };
    const ratio = Math.min(1, count / maxCell);
    // base rose palette; critical bucket skews darker so it reads first.
    const alpha = (isCritical ? 0.35 : 0.1) + ratio * (isCritical ? 0.6 : 0.65);
    return {
      bg: `rgba(138, 58, 58, ${alpha.toFixed(2)})`,
      color: ratio > 0.55 || isCritical ? '#FFFFFF' : '#2D2833',
    };
  };

  return (
    <Card className="rounded-2xl bg-white border border-[#E8E5EC] overflow-hidden" data-testid="dc-heatmap">
      <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#8A3A3A] flex items-center gap-1.5">
            <Clock size={12} /> Denial age heatmap — unanswered denials by payer × week
          </div>
          <div className="text-[11px] text-[#685B72] mt-0.5">
            Showing top {data.shown} of {data.payer_count} payer{data.payer_count === 1 ? '' : 's'}. Darker cells = more open claims. Click a cell to drill into that payer.
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[#685B72]">
          <span>low</span>
          <div className="h-2 w-24 rounded-full"
               style={{ background: 'linear-gradient(90deg, rgba(138,58,58,0.08) 0%, rgba(138,58,58,0.95) 100%)' }} />
          <span>high</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="dc-heatmap-table">
          <thead>
            <tr className="bg-[#FAF8FB] text-[#685B72]">
              <th className="px-3 py-2 text-left font-semibold">Payer</th>
              {buckets.map(b => (
                <th
                  key={b.key}
                  className={`px-3 py-2 text-center font-semibold ${b.key === '60+' ? 'text-[#8A3A3A]' : ''}`}
                >
                  {b.label}
                </th>
              ))}
              <th className="px-3 py-2 text-right font-semibold">Total</th>
              <th className="px-3 py-2 text-right font-semibold">Oldest</th>
            </tr>
          </thead>
          <tbody>
            {data.payers.map(p => (
              <tr key={p.payer} className="border-t border-[#E8E5EC]" data-testid={`dc-heatmap-row-${p.payer.replace(/\W+/g, '_')}`}>
                <td className="px-3 py-2 font-semibold text-[#2D2833] whitespace-nowrap">{p.payer}</td>
                {buckets.map(b => {
                  const n = p.cells[b.key] || 0;
                  const amt = p.amount_cells[b.key] || 0;
                  const tone = toneFor(n, b.key === '60+');
                  return (
                    <td key={b.key} className="p-1">
                      <button
                        type="button"
                        disabled={!n}
                        onClick={() => onCellClick && onCellClick(p.payer, b.key)}
                        className="w-full rounded-md py-2 px-1 text-center transition-opacity disabled:cursor-default hover:opacity-80"
                        style={{ background: tone.bg, color: tone.color }}
                        title={n ? `${n} claim${n === 1 ? '' : 's'}${amt > 0 ? ` · ${money(amt)}` : ''}` : 'no claims'}
                        data-testid={`dc-heatmap-cell-${p.payer.replace(/\W+/g, '_')}-${b.key}`}
                      >
                        <div className="font-mono text-[13px] font-bold leading-none">{n || '·'}</div>
                        {amt > 0 && n > 0 && (
                          <div className="text-[9px] mt-0.5 opacity-85">{money(amt)}</div>
                        )}
                      </button>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right tabular-nums font-bold text-[#2D2833]">{int(p.total)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${p.oldest_days >= 60 ? 'text-[#8A3A3A] font-bold' : 'text-[#685B72]'}`}>
                  {p.oldest_days}d
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#E8E5EC] bg-[#FAF8FB]">
              <td className="px-3 py-2 font-semibold text-[#685B72]">Total</td>
              {buckets.map(b => (
                <td key={b.key} className={`px-3 py-2 text-center tabular-nums font-bold ${b.key === '60+' ? 'text-[#8A3A3A]' : 'text-[#2D2833]'}`}>
                  {int(data.col_totals?.[b.key] || 0)}
                </td>
              ))}
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}


function BulkResolveDialog({ open, onClose, selectedKeys, config, rows, onDone }) {
  const [status, setStatus] = useState('Resolved');
  const [disposition, setDisposition] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setStatus('Resolved'); setDisposition(''); setNotes(''); }
  }, [open]);

  const totalAtRisk = rows.reduce((a, r) => a + (Number(r.billed_amount) || 0), 0);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/denied-claims/bulk-resolve', {
        claim_keys: selectedKeys,
        status, disposition, notes,
      });
      onDone(data.archived || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk resolve failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="dc-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#2D2833]">
            Bulk {status === 'Written Off' ? 'Write-Off' : 'Resolve'} — {selectedKeys.length} claims
          </DialogTitle>
        </DialogHeader>
        <div className="rounded-xl bg-[#FAF8FB] border border-[#E8E5EC] p-3 text-xs text-[#685B72] max-h-28 overflow-y-auto">
          {rows.slice(0, 10).map(r => (
            <div key={r.claim_key} className="flex items-center justify-between gap-2 py-0.5">
              <span className="truncate"><b className="text-[#2D2833]">{r.client}</b> · {r.payer_name} · {r.source_status}</span>
              <span className="font-mono text-[10px]">{r.billed_amount > 0 ? moneyExact(r.billed_amount) : '—'}</span>
            </div>
          ))}
          {rows.length > 10 && <div className="italic text-center pt-1">… and {rows.length - 10} more</div>}
          {totalAtRisk > 0 && (
            <div className="mt-2 pt-2 border-t border-[#E8E5EC] flex items-center justify-between font-semibold text-[#2D2833]">
              <span>Total $ at risk</span>
              <span>{moneyExact(totalAtRisk)}</span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-xs text-[#685B72]">Final status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="dc-bulk-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Resolved">Resolved</SelectItem>
                <SelectItem value="Written Off">Written Off</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#685B72]">Disposition (applied to all)</label>
            <Select
              value={disposition || '__none__'}
              onValueChange={(v) => setDisposition(v === '__none__' ? '' : v)}
            >
              <SelectTrigger className="rounded-xl mt-1" data-testid="dc-bulk-disposition"><SelectValue placeholder="Pick…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" className="italic text-[#685B72]">— Leave existing —</SelectItem>
                {(config?.dispositions || []).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#685B72]">Shared note (appended to each row)</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="rounded-xl mt-1" placeholder="e.g. Payer-wide reprocessing on 2026-02-15 cleared these batches" data-testid="dc-bulk-notes" />
          </div>
        </div>
        <div className="mt-3 rounded-xl bg-[#8A3A3A]/10 border border-[#8A3A3A]/30 p-3 text-xs text-[#8A3A3A]">
          <b>Heads up:</b> all {selectedKeys.length} claims will be <b>permanently archived</b>. Future Filed Claims uploads will <b>not</b> resurrect them.
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="dc-bulk-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#8A3A3A] hover:bg-[#6E2E2E] text-white rounded-xl" data-testid="dc-bulk-confirm">
            {saving ? 'Archiving…' : `Archive ${selectedKeys.length} claim${selectedKeys.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditClaimDialog({ row, config, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) setForm({
      status: row.status || 'New',
      disposition: row.disposition || '',
      notes: row.notes || '',
      ref_claim: row.ref_claim || '',
      owner: row.owner || '',
      next_follow_up: row.next_follow_up || '',
      billed_amount: row.billed_amount ?? '',
    });
  }, [row]);

  if (!row) return null;

  const save = async () => {
    setSaving(true);
    try {
      // Coerce billed_amount to a number; blank → 0 so the backend aggregator stays happy.
      const payload = { ...form };
      payload.billed_amount = Number(payload.billed_amount) || 0;
      const { data } = await api.patch(`/denied-claims/tracker/${row.claim_key}`, payload);
      onSaved(!!data.archived);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const terminal = form.status === 'Resolved' || form.status === 'Written Off';

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl" data-testid="dc-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#2D2833]">{row.client}</DialogTitle>
        </DialogHeader>

        {/* Lookup details — everything an admin needs to pull this up in SimplePractice */}
        <div className="rounded-xl bg-[#FAF8FB] border border-[#E8E5EC] p-3 grid grid-cols-2 gap-2 text-xs">
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">DOS / submitted</div><div className="text-[#2D2833]">{row.date_submitted ? fmtDate(row.date_submitted) : '—'}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Source status</div><div className="text-[#2D2833]">{row.source_status}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Payer</div><div className="text-[#2D2833]">{row.payer_name || row.raw_payer || '—'}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Payer code</div><div className="text-[#2D2833] font-mono">{row.raw_payer || '—'}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Payer Claim #</div><div className="text-[#2D2833] font-mono">{row.payer_claim || '—'}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Clearinghouse Ref</div><div className="text-[#2D2833] font-mono">{row.clearinghouse_ref || '—'}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Days aged</div><div className={`font-mono ${row.days_aged >= 60 ? 'text-[#8A3A3A] font-bold' : 'text-[#2D2833]'}`}>{row.days_aged}d</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">First flagged</div><div className="text-[#2D2833]">{row.date_first_flagged ? fmtDate(row.date_first_flagged) : '—'}</div></div>
        </div>

        {/* Admin fields */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="text-xs text-[#685B72]">Work status</label>
            <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="dc-field-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(config?.statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#685B72]">Disposition</label>
            <Select
              value={form.disposition || '__none__'}
              onValueChange={(v) => setForm(f => ({ ...f, disposition: v === '__none__' ? '' : v }))}
            >
              <SelectTrigger className="rounded-xl mt-1" data-testid="dc-field-disposition"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" className="italic text-[#685B72]">— None —</SelectItem>
                {(config?.dispositions || []).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#685B72]">Owner</label>
            <Input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} className="rounded-xl mt-1" data-testid="dc-field-owner" />
          </div>
          <div>
            <label className="text-xs text-[#685B72]">Next follow-up</label>
            <Input type="date" value={form.next_follow_up} onChange={e => setForm(f => ({ ...f, next_follow_up: e.target.value }))} className="rounded-xl mt-1" data-testid="dc-field-followup" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#685B72]">
              Billed amount <span className="italic text-[#685B72]/70">· drives $ at risk</span>
            </label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.billed_amount}
              onChange={e => setForm(f => ({ ...f, billed_amount: e.target.value }))}
              className="rounded-xl mt-1"
              placeholder="0.00"
              data-testid="dc-field-amount"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#685B72]">Reference claim # (resubmission)</label>
            <Input value={form.ref_claim} onChange={e => setForm(f => ({ ...f, ref_claim: e.target.value }))} className="rounded-xl mt-1" placeholder="e.g. corrected-claim ICN or SP claim #" data-testid="dc-field-refclaim" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#685B72]">Notes</label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="rounded-xl mt-1" rows={3} data-testid="dc-field-notes" />
          </div>
        </div>

        {terminal && (
          <div className="mt-3 rounded-xl bg-[#8A3A3A]/10 border border-[#8A3A3A]/30 p-3 text-xs text-[#8A3A3A]">
            <b>Heads up:</b> marking this claim <b>{form.status}</b> will archive it permanently. Future Filed Claims uploads will <b>not</b> resurrect this row.
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="dc-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl" data-testid="dc-edit-save">
            {saving ? 'Saving…' : terminal ? `Save & Archive (${form.status})` : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
